import { useEffect, useState, useCallback, useMemo } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Card, CardContent } from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Calendar as CalendarIcon,
  FileDown,
  Search,
  Send,
  Boxes,
  IndianRupee,
  Building2,
  ArrowDownToLine,
  ArrowUpFromLine,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  get_stock_issue_report,
  get_report_filter_data,
  server_post_data,
} from "../ServiceConnection/serviceconnection";
import XLSX from "sheetjs-style";
import { handleError } from "../CommonJquery/CommonJquery";

// Backend ab har transfer ki ek hi row bhejta hai (From → To)
const transferTypeBadgeClass = (type) => {
  switch (type) {
    case "TRANSFER":
      return "bg-primary/10 text-primary";
    case "ISSUED_TO_DEPARTMENT":
      return "bg-primary/10 text-primary";
    case "DEDUCTED_FROM_STORE":
      return "bg-muted text-muted-foreground";
    default:
      return "bg-muted text-muted-foreground";
  }
};

const transferTypeLabel = (type) => {
  switch (type) {
    case "TRANSFER":
      return "Transfer";
    case "ISSUED_TO_DEPARTMENT":
      return "Issued to Dept.";
    case "DEDUCTED_FROM_STORE":
      return "Deducted from Store";
    default:
      return type || "-";
  }
};

export default function StockIssueReport() {
  const { toast } = useToast();

  // Filter states
  const [filterOption, setFilterOption] = useState("");
  const [startCal, setStartCal] = useState(new Date());
  const [endCal, setEndCal] = useState(new Date());
  const [departmentId, setDepartmentId] = useState("all");
  const [ingredientId, setIngredientId] = useState("all");

  // Filter dropdown data
  const [departments, setDepartments] = useState([]);
  const [rawItems, setRawItems] = useState([]);

  // Data states
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [issueData, setIssueData] = useState([]);
  const [deptSummary, setDeptSummary] = useState({});
  const [indentSummary, setIndentSummary] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 10;

  // Stats (from API summary)
  const [stats, setStats] = useState({
    totalEntries: 0,
    totalIssuedQty: 0,
    totalIssuedValue: 0,
    departmentsCount: 0,
  });

  // Map department_id -> department_name, since the API only returns department_id
  const departmentMap = useMemo(() => {
    const map = {};
    departments.forEach((d) => {
      map[String(d.primary_id)] = d.department_name;
    });
    return map;
  }, [departments]);

  const departmentName = (id) => departmentMap[String(id)] || `Dept #${id}`;

  // Format number
  const formatNumber = (num) => {
    if (num === "-") return "-";
    if (num === undefined || num === null) return 0;
    const parsed = parseFloat(num);
    if (isNaN(parsed)) return 0;
    return parsed % 1 === 0 ? parsed : parseFloat(parsed.toFixed(2));
  };

  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Fetch dropdown filter data (departments + ingredients)
  const fetchFilterData = useCallback(async () => {
    try {
      const fd = new FormData();
      const response = await server_post_data(get_report_filter_data, fd);
      if (!response.data.error) {
        setDepartments(response.data.message.departments || []);
        setRawItems(response.data.message.raw_items || []);
      }
    } catch (error) {
      console.error("Error fetching filter data:", error);
    }
  }, []);

  // Fetch stock issue data
  const fetchIssueData = useCallback(
    async (start_date, end_date, flag = "1") => {
      if (flag === "2") {
        if (filterOption === "today") {
          const today = new Date();
          start_date = new Date(today.setHours(0, 0, 0, 0));
          end_date = new Date(today.setHours(23, 59, 59, 999));
        } else if (filterOption === "this_week") {
          const today = new Date();
          const firstDayOfWeek = new Date(
            today.setDate(today.getDate() - today.getDay()),
          );
          start_date = new Date(firstDayOfWeek.setHours(0, 0, 0, 0));
          end_date = new Date(today.setHours(23, 59, 59, 999));
        } else if (filterOption === "this_month") {
          const today = new Date();
          start_date = new Date(
            today.getFullYear(),
            today.getMonth(),
            1,
            0,
            0,
            0,
          );
          end_date = new Date(
            today.getFullYear(),
            today.getMonth() + 1,
            0,
            23,
            59,
            59,
          );
        } else if (filterOption === "this_year") {
          const today = new Date();
          start_date = new Date(today.getFullYear(), 0, 1, 0, 0, 0);
          end_date = new Date(today.getFullYear(), 11, 31, 23, 59, 59);
        } else if (filterOption === "custom") {
          if (!startCal || !endCal) {
            toast({
              title: "Validation Error",
              description: "Please select both start and end dates.",
              variant: "destructive",
            });
            return;
          }
          start_date = startCal;
          end_date = endCal;
        }

        if (start_date > end_date) {
          toast({
            title: "Validation Error",
            description: "Start date cannot be greater than end date.",
            variant: "destructive",
          });
          return;
        }
      }

      setShowLoaderAdmin(true);
      const fd = new FormData();
      fd.append("start_date", formatDateLocal(start_date));
      fd.append("end_date", formatDateLocal(end_date));
      if (departmentId !== "all") fd.append("department_id", departmentId);
      if (ingredientId !== "all") fd.append("ingredient_id", ingredientId);

      try {
        const response = await server_post_data(get_stock_issue_report, fd);

        if (response.data.error) {
          toast({
            title: "Error",
            description: response.data.message,
            variant: "destructive",
          });
        } else {
          const raw = response.data.data || [];
          const summary = response.data.summary || {};

          // Ek transfer = ek row. Pehle OUT + IN dono aate the — double count.
          const data = raw.map((r) => ({
            ...r,
            transfer_type: "TRANSFER",
            route: `${r.from_department || "—"} → ${r.to_department || "—"}`,
            indent_id: r.indent_no || r.indent_id,
          }));

          // Department summary ab naam se (issued_to), qty nahi — alag unit
          // ki quantity jodna galat tha
          const deptWise = {};
          (summary.issued_to || []).forEach((d) => {
            deptWise[d.department] = { amount: d.value, count: d.count };
          });

          const indentWise = {};
          data.forEach((r) => {
            const k = r.indent_id || "-";
            if (!indentWise[k]) indentWise[k] = { amount: 0, count: 0 };
            indentWise[k].amount += Number(r.value) || 0;
            indentWise[k].count += 1;
          });

          setIssueData(data);
          setDeptSummary(deptWise);
          setIndentSummary(indentWise);
          setStats({
            totalEntries: summary.total_transfers ?? data.length,
            totalIssuedQty: (summary.ingredient_wise || []).length,
            totalIssuedValue: summary.total_value ?? 0,
            departmentsCount: Object.keys(deptWise).length,
          });
        }
      } catch (error) {
        console.error("Error fetching stock issue data:", error);
        toast({
          title: "Error",
          description: "Failed to load stock issue data",
          variant: "destructive",
        });
      } finally {
        setShowLoaderAdmin(false);
      }
    },
    [filterOption, startCal, endCal, departmentId, ingredientId, toast],
  );

  // Handle search
  const handleSearch = useCallback(() => {
    setCurrentPage(1);
    fetchIssueData(startCal, endCal, "2");
  }, [startCal, endCal, fetchIssueData]);

  // Excel export - 3 sheets (issue detail + department summary + indent summary)
  function downloadExcel(orderData, filename = "Stock_Issue_Report.xlsx") {
    if (!orderData.length) {
      handleError("No data available for export!");
      return;
    }

    // Sheet 1: Issue detail (only fields the API actually returns)
    const issueSheetData = orderData.map((item, index) => ({
      "Sr. No.": index + 1,
      Date: item.date || "-",
      Ingredient: item.ingredient_name || "-",
      Department: item.route || departmentName(item.department_id),
      Movement: transferTypeLabel(item.transfer_type),
      Quantity: formatNumber(item.quantity),
      Unit: item.unit || "-",
      "Rate (₹)": formatNumber(item.rate),
      "Qty Before": formatNumber(item.qty_before),
      "Qty Current": formatNumber(item.qty_current),
      "Indent ID": item.indent_id || "-",
    }));

    issueSheetData.push({
      "Sr. No.": "",
      Date: "",
      Ingredient: "",
      Department: "",
      Movement: "TOTAL",
      Quantity: "",
      Unit: "",
      "Rate (₹)": "",
      "Qty Before": "",
      "Qty Current": "",
      "Indent ID": `${stats.totalEntries} entries`,
    });

    // Sheet 2: Department-wise summary (keyed by department_id in the API)
    const deptSheetData = Object.entries(deptSummary).map(
      ([deptId, info], index) => ({
        "Sr. No.": index + 1,
        Department: deptId,
        "Transfers": info.count,
        "Issued Value (₹)": formatNumber(info.amount),
      }),
    );

    // Sheet 3: Indent-wise summary (keyed by indent_id / bill_id in the API)
    const indentSheetData = Object.entries(indentSummary).map(
      ([indentId, info], index) => ({
        "Sr. No.": index + 1,
        "Indent ID": indentId,
        "Items": info.count,
        "Value (₹)": formatNumber(info.amount),
      }),
    );

    const issueSheet = XLSX.utils.json_to_sheet(issueSheetData);
    issueSheet["!cols"] = [
      { wch: 8 },
      { wch: 20 },
      { wch: 22 },
      { wch: 18 },
      { wch: 18 },
      { wch: 10 },
      { wch: 8 },
      { wch: 10 },
      { wch: 12 },
      { wch: 12 },
      { wch: 14 },
    ];

    const deptSheet = XLSX.utils.json_to_sheet(deptSheetData);
    deptSheet["!cols"] = [
      { wch: 8 },
      { wch: 22 },
      { wch: 12 },
      { wch: 12 },
      { wch: 16 },
    ];

    const indentSheet = XLSX.utils.json_to_sheet(indentSheetData);
    indentSheet["!cols"] = [
      { wch: 8 },
      { wch: 14 },
      { wch: 12 },
      { wch: 10 },
      { wch: 14 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, issueSheet, "Stock Issues");
    XLSX.utils.book_append_sheet(workbook, deptSheet, "Department Summary");
    if (indentSheetData.length) {
      XLSX.utils.book_append_sheet(workbook, indentSheet, "Indent Summary");
    }
    XLSX.writeFile(workbook, filename);
  }

  // Pagination
  const totalPages = Math.ceil(issueData.length / entriesPerPage);
  const currentData = issueData.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage,
  );

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };

  // Format date for table display (API sends 'YYYY-MM-DD HH:MM:SS')
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString.replace(" ", "T")).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Initial load
  useEffect(() => {
    fetchFilterData();
    fetchIssueData(startCal, endCal, "1");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MainLayout>
      <Header
        title="Stock Issue Report"
        subtitle="Material issued from Main Store to kitchens and departments"
      />

      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Send className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.totalEntries}
                </p>
                <p className="text-sm text-muted-foreground">Total Entries</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                <Boxes className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.totalIssuedQty}
                </p>
                <p className="text-sm text-muted-foreground">
                  Ingredients Moved
                </p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <IndianRupee className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  ₹{formatNumber(stats.totalIssuedValue)}
                </p>
                <p className="text-sm text-muted-foreground">
                  Total Issued Value
                </p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.departmentsCount}
                </p>
                <p className="text-sm text-muted-foreground">Departments</p>
              </div>
            </div>
          </div>
        </div>

        {/* Department-wise Summary Chips (API keys these by department_id, not name) */}
        {Object.keys(deptSummary).length > 0 && (
          <div className="flex flex-wrap gap-2">
            {Object.entries(deptSummary).map(([deptId, info]) => (
              <span
                key={deptId}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-primary/10 text-primary text-xs font-medium"
              >
                To {deptId}: ₹{formatNumber(info.amount)} ({info.count})
              </span>
            ))}
          </div>
        )}

        {/* Filters Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Date Filter Dropdown */}
              <div className="space-y-2">
                <Label>Filter</Label>
                <Select value={filterOption} onValueChange={setFilterOption}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="this_week">This Week</SelectItem>
                    <SelectItem value="this_month">This Month</SelectItem>
                    <SelectItem value="this_year">This Year</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !startCal && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startCal ? (
                          format(startCal, "dd MMM yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startCal}
                        onSelect={setStartCal}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !endCal && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endCal ? (
                          format(endCal, "dd MMM yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endCal}
                        onSelect={setEndCal}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {/* Department */}
              <div className="space-y-2">
                <Label>Department</Label>
                <Select value={departmentId} onValueChange={setDepartmentId}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Departments" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map((dept) => (
                      <SelectItem
                        key={dept.primary_id}
                        value={String(dept.primary_id)}
                      >
                        {dept.department_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Ingredient */}
              <div className="space-y-2">
                <Label>Ingredient</Label>
                <Select value={ingredientId} onValueChange={setIngredientId}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Ingredients" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Ingredients</SelectItem>
                    {rawItems.map((item) => (
                      <SelectItem
                        key={item.primary_id}
                        value={String(item.primary_id)}
                      >
                        {item.ingredient_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Action Buttons */}
              <div className="flex items-end gap-2">
                <Button
                  onClick={handleSearch}
                  className="gap-2"
                  disabled={showLoaderAdmin}
                >
                  <Search className="w-4 h-4" />
                  Search
                </Button>
                <Button
                  variant="outline"
                  onClick={() => downloadExcel(issueData)}
                  className="gap-2"
                  disabled={issueData.length === 0}
                >
                  <FileDown className="w-4 h-4" />
                  Export Excel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table Card */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground">Sr.No</TableHead>
                  <TableHead className="text-muted-foreground">Date</TableHead>
                  <TableHead className="text-muted-foreground">
                    Ingredient
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Department
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Movement
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Quantity
                  </TableHead>
                  <TableHead className="text-muted-foreground">Unit</TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Rate (₹)
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Qty Before
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Qty Current
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Indent ID
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {showLoaderAdmin ? (
                  <TableRow>
                    <TableCell
                      colSpan={11}
                      className="text-center text-muted-foreground"
                    >
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : currentData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={11}
                      className="text-center text-muted-foreground"
                    >
                      No data found
                    </TableCell>
                  </TableRow>
                ) : (
                  currentData.map((row, index) => (
                    <TableRow
                      key={row.log_id || index}
                      className="border-border"
                    >
                      <TableCell>
                        {(currentPage - 1) * entriesPerPage + index + 1}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {formatDate(row.date)}
                      </TableCell>
                      <TableCell className="font-medium">
                        {row.ingredient_name || "-"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {row.route || departmentName(row.department_id)}
                      </TableCell>
                      <TableCell>
                        <span
                          className={cn(
                            "inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium",
                            transferTypeBadgeClass(row.transfer_type),
                          )}
                        >
                          {row.transfer_type === "ISSUED_TO_DEPARTMENT" ? (
                            <ArrowDownToLine className="w-3 h-3" />
                          ) : (
                            <ArrowUpFromLine className="w-3 h-3" />
                          )}
                          {transferTypeLabel(row.transfer_type)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatNumber(row.quantity)}
                      </TableCell>
                      <TableCell>{row.unit || "-"}</TableCell>
                      <TableCell className="text-right">
                        {formatNumber(row.rate)}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {formatNumber(row.qty_before)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatNumber(row.qty_current)}
                      </TableCell>
                      <TableCell>{row.indent_id || "-"}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-border">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * entriesPerPage + 1} to{" "}
                {Math.min(currentPage * entriesPerPage, issueData.length)} of{" "}
                {issueData.length} entries
              </div>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 &&
                      pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <Button
                        key={pageNumber}
                        variant={
                          pageNumber === currentPage ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </Button>
                    );
                  } else if (
                    pageNumber === currentPage - 2 ||
                    pageNumber === currentPage + 2
                  ) {
                    return <span key={pageNumber}>...</span>;
                  }
                  return null;
                })}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}